import { useEffect, useState } from "react";
import { api, formatNum, formatTRY, toNum } from "@/lib/apiClient";
import { groupByCategory } from "@/lib/groupBy";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Boxes, PackagePlus, PackageMinus, AlertTriangle, Eraser } from "lucide-react";
import { toast } from "sonner";
import { keepScroll } from "@/lib/keepScroll";

export default function Stock() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState(null);
  const [amount, setAmount] = useState(0);
  const [mode, setMode] = useState("package");
  const [pkgGrams, setPkgGrams] = useState("");
  const [direction, setDirection] = useState("in");
  const [valueDetail, setValueDetail] = useState(null);

  const pkg = toNum(pkgGrams) || 0;
  const absGrams = mode === "package" ? (toNum(amount) || 0) * pkg : (toNum(amount) || 0);
  const grams = direction === "out" ? -absGrams : absGrams;

  const load = () => keepScroll(() => api.get("/ingredients").then((r) => setItems(r.data.filter((i) => i.retail_source !== "pdf"))));

  const resetAll = async () => {
    if (!window.confirm(`TÜM malzemelerin stoğu 0 yapılacak (${items.length} malzeme). Bu işlem geri alınamaz. Devam edilsin mi?`)) return;
    if (!window.confirm("Emin misiniz? Tüm stok miktarları sıfırlanacak.")) return;
    try { const r = await api.post("/ingredients/reset-stock"); toast.success(`${r.data.reset} malzemenin stoğu sıfırlandı`); load(); }
    catch { toast.error("Sıfırlanamadı"); }
  };
  useEffect(() => { load(); }, []);

  const openRestock = (it, dir = "in") => { setTarget(it); setDirection(dir); setAmount(0); setPkgGrams(it.package_grams > 0 ? it.package_grams : ""); setMode("package"); setRecordExpense(true); setCostOverride(""); setOpen(true); };
  const [recordExpense, setRecordExpense] = useState(true);
  const [costOverride, setCostOverride] = useState("");
  const restock = async () => {
    if (mode === "package" && !(pkg > 0)) return toast.error("Paket gramajını girin");
    if (!(absGrams > 0)) return toast.error("Miktar girin");
    if (direction === "out" && absGrams > (toNum(target.current_stock) || 0)) return toast.error("Stoktan fazla çıkış yapılamaz");
    const body = { amount: grams, record_expense: direction === "in" && recordExpense };
    if (body.record_expense && String(costOverride).trim() !== "") body.cost = toNum(costOverride) || 0;
    const r = await api.post(`/ingredients/${target.id}/restock`, body);
    toast.success(direction === "out" ? `${target.name} stoğundan ${formatNum(absGrams, 0)} ${target.unit} çıkarıldı`
      : r.data.expense ? `${target.name} stoğu güncellendi · ${formatTRY(r.data.expense.amount)} gider olarak yazıldı` : `${target.name} stoğu güncellendi`);
    keepScroll(async () => { setOpen(false); await load(); });
  };
  const addedValue = absGrams / (target?.unit === "adet" ? 1 : 100) * (toNum(target?.cost_per_100g) || 0);

  const low = items.filter((i) => i.current_stock <= i.min_stock_alert);
  const base = (it) => (it.unit === "adet" ? 1 : 100);
  const stockValue = (it) => (toNum(it.current_stock) || 0) / base(it) * (toNum(it.cost_per_100g) || 0);
  const totalValue = items.reduce((a, it) => a + stockValue(it), 0);
  const hasVp = (it) => (toNum(it.vp_per_100g) || 0) > 0 || (toNum(it.package_vp) || 0) > 0;
  const vpValue = items.filter(hasVp).reduce((a, it) => a + stockValue(it), 0);
  const nonVpValue = totalValue - vpValue;
  const stockVp = (it) => (it.package_grams > 0 && it.package_vp > 0
    ? (toNum(it.current_stock) || 0) / it.package_grams * it.package_vp
    : (toNum(it.current_stock) || 0) / base(it) * (toNum(it.vp_per_100g) || 0));
  const totalVp = items.reduce((a, it) => a + stockVp(it), 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">Stok Takibi</h1>
          <p className="mt-1 text-sm text-muted-foreground">Malzeme stok seviyeleri ve kritik uyarılar</p>
          <Button size="sm" variant="outline" className="mt-2 border-destructive/50 text-destructive hover:bg-destructive/10" data-testid="reset-stock-btn" onClick={resetAll}>
            <Eraser className="mr-1 h-3.5 w-3.5" /> Stoğu Sıfırla (tümü 0)
          </Button>
        </div>
        <div className="rounded-xl border border-border bg-card px-5 py-3" data-testid="stock-value-summary">
          <div className="grid grid-cols-2 gap-6 text-right">
            <button type="button" className="rounded-lg px-2 py-1 text-right transition-colors hover:bg-secondary/40" data-testid="vp-stock-card" onClick={() => setValueDetail("vp")} title="VP'li malzemeleri gör">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">VP'li Stok Değeri</p>
              <p className="font-heading text-xl font-extrabold underline decoration-dotted underline-offset-4" style={{ color: "var(--chart-5)" }} data-testid="vp-stock-value">{formatTRY(vpValue)}</p>
              <p className="text-[11px] text-muted-foreground">Toplam VP: <span className="font-mono font-semibold" data-testid="total-stock-vp">{formatNum(totalVp, 2)}</span></p>
            </button>
            <button type="button" className="rounded-lg px-2 py-1 text-right transition-colors hover:bg-secondary/40" data-testid="nonvp-stock-card" onClick={() => setValueDetail("nonvp")} title="VP'siz malzemeleri gör">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">VP'siz Stok Değeri</p>
              <p className="font-heading text-xl font-extrabold text-foreground underline decoration-dotted underline-offset-4" data-testid="nonvp-stock-value">{formatTRY(nonVpValue)}</p>
              <p className="text-[11px] text-muted-foreground">{items.filter((it) => !hasVp(it)).length} malzeme</p>
            </button>
          </div>
          <div className="mt-2 flex items-baseline justify-between gap-6 border-t border-border pt-2">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Toplam Stok Değeri</p>
            <p className="font-heading text-2xl font-extrabold text-primary" data-testid="total-stock-value">{formatTRY(totalValue)}</p>
          </div>
        </div>
      </div>

      {low.length > 0 && (
        <div className="flex items-center gap-3 rounded-xl border border-destructive/30 bg-destructive/10 p-4" data-testid="stock-alert-banner">
          <AlertTriangle className="h-5 w-5 text-destructive" />
          <p className="text-sm font-medium text-destructive">
            {low.length} malzeme kritik stok seviyesinin altında! ({low.map((l) => l.name).join(", ")})
          </p>
        </div>
      )}

      <div className="overflow-x-auto rounded-xl border border-border bg-card" data-testid="stock-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-[11px] uppercase tracking-wide text-muted-foreground">
              <th className="px-4 py-3 font-semibold">Malzeme</th>
              <th className="px-3 py-3 text-right font-semibold">Stok</th>
              <th className="px-3 py-3 text-right font-semibold">Paket</th>
              <th className="px-3 py-3 font-semibold">Seviye</th>
              <th className="px-3 py-3 text-right font-semibold">Min</th>
              <th className="px-3 py-3 text-right font-semibold">Maliyet</th>
              <th className="px-3 py-3 text-right font-semibold">Stok Değeri</th>
              <th className="px-3 py-3 text-right font-semibold">Stok VP</th>
              <th className="px-3 py-3 text-right font-semibold">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {groupByCategory(items).map((g) => [
              <tr key={`h-${g.category}`} className="bg-secondary/30" data-testid={`category-header-${g.category}`}>
                <td colSpan={9} className="px-4 py-2 font-heading text-xs font-extrabold uppercase tracking-[0.2em] text-primary">{g.category} <span className="ml-2 rounded-full bg-secondary px-2 py-0.5 text-[11px] font-normal normal-case tracking-normal text-muted-foreground">{g.items.length} malzeme</span></td>
              </tr>,
              ...g.items.map((it) => {
                const max = Math.max(it.current_stock, it.min_stock_alert * 2, 1);
                const pct = Math.max(0, Math.min(100, (it.current_stock / max) * 100));
                const isLow = it.current_stock <= it.min_stock_alert;
                const isNeg = it.current_stock < 0;
                return (
                  <tr key={it.id} data-testid={`stock-card-${it.id}`} className={`border-b border-border/60 last:border-0 ${isLow ? "bg-destructive/5" : "hover:bg-secondary/20"}`}>
                    <td className="px-4 py-2.5">
                      <span className="font-semibold">{it.name}</span>
                      {isNeg ? <span className="ml-2 rounded-full bg-destructive px-2 py-0.5 text-[10px] font-bold text-destructive-foreground" data-testid={`stock-negative-${it.id}`}>EKSİ STOK</span>
                        : isLow && <span className="ml-2 animate-pulse rounded-full bg-destructive/15 px-2 py-0.5 text-[10px] font-semibold text-destructive">KRİTİK</span>}
                    </td>
                    <td className={`px-3 py-2.5 text-right font-mono font-bold ${isNeg ? "text-destructive" : ""}`}>{isNeg ? "−" : ""}{formatNum(Math.abs(it.current_stock), 0)} <span className="text-xs font-normal text-muted-foreground">{it.unit}</span></td>
                    <td className="px-3 py-2.5 text-right text-xs text-muted-foreground">{it.package_grams > 0 ? <span data-testid={`stock-packages-${it.id}`}>≈ {formatNum(it.current_stock / it.package_grams, 1)}</span> : "—"}</td>
                    <td className="px-3 py-2.5"><div className="h-1.5 w-24 overflow-hidden rounded-full bg-secondary"><div className="h-full rounded-full" style={{ width: `${pct}%`, background: isLow ? "var(--destructive)" : "var(--primary)" }} /></div></td>
                    <td className="px-3 py-2.5 text-right font-mono text-xs text-muted-foreground">{formatNum(it.min_stock_alert, 0)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-xs text-muted-foreground">{formatTRY(it.cost_per_100g)}<span className="text-[10px]">/{it.unit === "adet" ? "adet" : `100${it.unit}`}</span></td>
                    <td className="px-3 py-2.5 text-right font-mono font-semibold" data-testid={`stock-value-${it.id}`}>{formatTRY(stockValue(it))}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-xs" style={{ color: "var(--chart-5)" }}>{it.vp_per_100g > 0 ? <span data-testid={`stock-vp-${it.id}`}>{formatNum(stockVp(it), 2)}</span> : <span className="text-muted-foreground">—</span>}</td>
                    <td className="px-3 py-2.5">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="outline" className="h-7" data-testid={`stockout-btn-${it.id}`} onClick={() => openRestock(it, "out")} disabled={!(it.current_stock > 0)}><PackageMinus className="mr-1 h-3.5 w-3.5 text-destructive" /> Çıkar</Button>
                        <Button size="sm" variant="secondary" className="h-7" data-testid={`restock-btn-${it.id}`} onClick={() => openRestock(it, "in")}><PackagePlus className="mr-1 h-3.5 w-3.5" /> Ekle</Button>
                      </div>
                    </td>
                  </tr>
                );
              }),
            ])}
          </tbody>
        </table>
        {items.length === 0 && (
          <div className="py-16 text-center text-muted-foreground"><Boxes className="mx-auto mb-2 h-8 w-8 opacity-40" /> Malzeme bulunamadı</div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle>{direction === "out" ? "Stok Çıkar" : "Stok Ekle"} — {target?.name}</DialogTitle></DialogHeader>
          <div>
            <div className="mb-3 flex gap-1 rounded-lg bg-secondary/60 p-1" data-testid="restock-direction">
              {[["in", "Ekle (+)"], ["out", "Çıkar (−)"]].map(([d, l]) => (
                <button key={d} type="button" data-testid={`restock-direction-${d}`} onClick={() => setDirection(d)}
                  className={`flex-1 rounded-md px-2 py-1.5 text-xs font-semibold transition-colors ${direction === d ? (d === "out" ? "bg-destructive text-destructive-foreground" : "bg-primary text-primary-foreground") : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
              ))}
            </div>
            <div className="mb-3 flex gap-1 rounded-lg bg-secondary/60 p-1" data-testid="restock-mode">
              {[["package", "Paket / Adet"], ["unit", target?.unit || "g"]].map(([m, l]) => (
                <button key={m} type="button" data-testid={`restock-mode-${m}`} onClick={() => setMode(m)}
                  className={`flex-1 rounded-md px-2 py-1.5 text-xs font-semibold transition-colors ${mode === m ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
              ))}
            </div>
            {mode === "package" ? (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Paket Sayısı</Label>
                  <Input data-testid="restock-amount" type="text" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1" autoFocus />
                </div>
                <div>
                  <Label className="text-xs">Paket Gramajı ({target?.unit})</Label>
                  <Input data-testid="restock-package-grams" type="text" inputMode="decimal" value={pkgGrams} placeholder="örn. 500" onChange={(e) => setPkgGrams(e.target.value)} className="mt-1" />
                </div>
              </div>
            ) : (
              <div>
                <Label className="text-xs">{direction === "out" ? "Çıkarılacak" : "Eklenecek"} Miktar ({target?.unit})</Label>
                <Input data-testid="restock-amount" type="text" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1" autoFocus />
              </div>
            )}
            <p className="mt-2 text-xs text-muted-foreground" data-testid="restock-preview">
              {mode === "package" && `${formatNum(toNum(amount) || 0, 0)} × ${formatNum(pkg, 0)} ${target?.unit} = ${direction === "out" ? "−" : "+"}${formatNum(absGrams, 0)} ${target?.unit} · `}
              Yeni toplam: {formatNum((toNum(target?.current_stock) || 0) + grams, 0)} {target?.unit}
              {target?.package_grams > 0 && ` (≈ ${formatNum(((toNum(target?.current_stock) || 0) + grams) / target.package_grams, 1)} paket)`}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              {direction === "out" ? "Çıkan stok değeri" : "Eklenen stok değeri"}: {formatTRY(addedValue)}
            </p>
            {direction === "in" && (
              <div className="mt-3 rounded-lg border border-border bg-background/50 p-3" data-testid="restock-expense-box">
                <label className="flex items-center justify-between gap-2 text-xs font-semibold">
                  <span>Gider olarak Gelir/Gider'e yaz</span>
                  <Switch checked={recordExpense} onCheckedChange={setRecordExpense} data-testid="restock-expense-toggle" />
                </label>
                {recordExpense && (
                  <div className="mt-2">
                    <Label className="text-[11px] text-muted-foreground">Ödenen tutar (₺) — boş bırakılırsa {formatTRY(addedValue)}</Label>
                    <Input data-testid="restock-cost" type="text" inputMode="decimal" value={costOverride} placeholder={formatNum(addedValue, 2)} onChange={(e) => setCostOverride(e.target.value)} className="mt-1 h-8" />
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button data-testid="confirm-restock-btn" onClick={restock} variant={direction === "out" ? "destructive" : "default"} className="font-semibold">{direction === "out" ? "Stoktan Çıkar" : "Stoğa Ekle"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!valueDetail} onOpenChange={(o) => !o && setValueDetail(null)}>
        <DialogContent className="sm:max-w-2xl" data-testid="stock-value-dialog">
          <DialogHeader><DialogTitle>{valueDetail === "vp" ? "VP'li Malzemeler" : "VP'siz Malzemeler"}</DialogTitle></DialogHeader>
          {(() => {
            const list = items.filter((it) => (valueDetail === "vp" ? hasVp(it) : !hasVp(it))).sort((a, b) => stockValue(b) - stockValue(a));
            const sum = list.reduce((a, it) => a + stockValue(it), 0);
            return (
              <>
                <p className="text-xs text-muted-foreground">{list.length} malzeme · stok değerine göre sıralı</p>
                <div className="max-h-[60vh] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-card text-xs text-muted-foreground"><tr><th className="py-1.5 text-left">Malzeme</th><th className="py-1.5 text-right">Stok</th><th className="py-1.5 text-right">Stok Değeri</th>{valueDetail === "vp" && <th className="py-1.5 text-right">Stok VP</th>}</tr></thead>
                    <tbody className="divide-y divide-border">
                      {list.map((it) => (
                        <tr key={it.id} data-testid={`stock-value-row-${it.id}`}>
                          <td className="py-2 font-medium">{it.name}<span className="ml-2 text-xs text-muted-foreground">{it.category}</span></td>
                          <td className={`py-2 text-right font-mono ${it.current_stock < 0 ? "font-bold text-destructive" : ""}`}>{it.current_stock < 0 ? "−" : ""}{formatNum(Math.abs(it.current_stock), 0)} {it.unit}</td>
                          <td className="py-2 text-right font-mono font-semibold">{formatTRY(stockValue(it))}</td>
                          {valueDetail === "vp" && <td className="py-2 text-right font-mono text-xs">{formatNum(stockVp(it), 2)}</td>}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex items-center justify-between border-t border-border pt-3">
                  <span className="text-xs uppercase tracking-wide text-muted-foreground">Toplam</span>
                  <span className="font-heading text-xl font-extrabold" data-testid="stock-value-dialog-total">{formatTRY(sum)}</span>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
